import type { MirrorLiveToolHandoff } from "@/hooks/useMirrorLive";
import { MIRROR_APP_HANDOFF_URL } from "../../supabase/functions/_shared/mirrorChannelOutcome";

export interface MirrorReviewIdentity { readonly ownerId: string; readonly authSessionId: string }
export const MIRROR_REVIEW_DRAFT_TTL_MS = 120_000;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const sourceIdentifier = (value: unknown): value is string => typeof value === "string" && /^[A-Za-z0-9_#-]{1,160}$/u.test(value);
const callIdentifier = (value: unknown): value is string => typeof value === "string" && /^call_[A-Za-z0-9_-]{1,160}$/u.test(value);
const same = (a: MirrorReviewIdentity | null, b: MirrorReviewIdentity | null) =>
  a === null || b === null ? a === b : a.ownerId === b.ownerId && a.authSessionId === b.authSessionId;
const identityOf = (value: MirrorReviewIdentity | null): MirrorReviewIdentity | null =>
  value && uuid.test(value.ownerId) && uuid.test(value.authSessionId)
    ? Object.freeze({ ownerId: value.ownerId, authSessionId: value.authSessionId }) : null;

/** A draft only. Taking it cannot send a turn, execute an action or authorize a provider. */
export function createMirrorChannelReviewDraftStore(options: {
  now?: () => number; nonce?: () => string;
} = {}) {
  const now = options.now ?? Date.now;
  const nonce = options.nonce ?? (() => crypto.randomUUID());
  let identity: MirrorReviewIdentity | null = null, generation = 0;
  let entry: { id: string; text: string; actor: MirrorReviewIdentity; generation: number; expires: number } | null = null;
  let cleanup: (() => void) | null = null;
  const clear = () => {
    entry = null;
    const previous = cleanup;
    cleanup = null;
    previous?.();
  };
  return {
    observe(value: MirrorReviewIdentity | null) {
      const next = identityOf(value);
      if (!same(identity, next)) { generation++; clear(); }
      identity = next;
    },
    stage(handoff: MirrorLiveToolHandoff, actor: MirrorReviewIdentity): string | null {
      if (!same(identity, identityOf(actor)) || !identity
        || !handoff.handoff || handoff.handoff.type !== "app_handoff"
        || handoff.handoff.url !== MIRROR_APP_HANDOFF_URL || handoff.handoff.executed !== false || handoff.handoff.receipt !== null
        || !["app_review", "app_only", "native_required"].includes(handoff.handoff.reason)
        || handoff.ownerId !== identity.ownerId || handoff.authSessionId !== identity.authSessionId
        || !uuid.test(handoff.liveSessionId) || !uuid.test(handoff.runNonce) || !uuid.test(handoff.sessionId)
        || !sourceIdentifier(handoff.sourceItemId) || !callIdentifier(handoff.callId)
        || typeof handoff.transcript !== "string" || !handoff.transcript.trim() || handoff.transcript.length > 4000
        || typeof handoff.response !== "string" || handoff.response.length > 16_000) return null;
      const id = nonce();
      if (!uuid.test(id)) return null;
      clear();
      entry = { id, text: handoff.transcript, actor: identity, generation, expires: now() + MIRROR_REVIEW_DRAFT_TTL_MS };
      return id;
    },
    attachCleanup(id: string, release: () => void) {
      if (entry?.id !== id) { release(); return; }
      const previous = cleanup;
      cleanup = release;
      previous?.();
    },
    take(id: string, actor: MirrorReviewIdentity): string | null {
      if (!entry || entry.id !== id) return null;
      const captured = entry;
      clear();
      return captured.expires > now() && captured.generation === generation
        && same(identity, identityOf(actor)) && same(captured.actor, identity) ? captured.text : null;
    },
    discard(id?: string) { if (id === undefined || entry?.id === id) clear(); },
  };
}

/** Memory survives the LIVE -> chat route only. Nothing is persisted to disk or a URL. */
export const mirrorChannelReviewDrafts = createMirrorChannelReviewDraftStore();
